// src/services/workspaceService.ts
// Domain-specific service for workspace operations

import { apiClient } from "./api";
import { API_ENDPOINTS } from "../constants/index";
import {
  normalizeWorkspace,
  normalizeGroup,
  normalizeTile,
  type NormalizedWorkspace,
  type NormalizedGroup,
  type NormalizedTile,
  type RawWorkspace,
} from "../utils/dataUtils";
import type {
  DashboardGroupRead,
  DashboardReminder,
  Note,
} from "../types/entities";
import { withCache, cache, CACHE_KEYS } from "../utils/cacheUtils";
import { logger } from "@common/utils/logger";

// Helper function to sort groups and their tiles by position
function sortGroupsAndTiles<
  T extends { position?: number; tiles?: { position?: number }[] },
>(groups: T[]): T[] {
  return groups
    .map((group) => ({
      ...group,
      // Sort tiles by position if they have a position field
      tiles: group.tiles
        ? [...group.tiles].sort((a, b) => (a.position || 0) - (b.position || 0))
        : [],
    }))
    .sort((a, b) => (a.position || 0) - (b.position || 0));
}

// Type definitions
export type { RawWorkspace };

/** GET /workspaces/{id}: the workspace with its dashboard, normalized. */
export interface WorkspaceDetails extends RawWorkspace {
  workspace_id: number;
  workspace_name: string;
  view: string;
  owner_email: string;
  tile_groups_v1: NormalizedGroup[];
  note_groups_v1: NormalizedGroup[];
  note_reminders: DashboardReminder[];
}

/** `WorkspaceWithDashboardRead`: what GET /workspaces/{id} returns. */
interface WorkspaceWithDashboardRead extends RawWorkspace {
  workspace_id: number;
  workspace_name: string;
  view: string;
  owner_email: string;
}

export interface WorkspaceWithGroups extends NormalizedWorkspace {
  groups: WorkspaceGroup[];
}

export interface WorkspaceGroup {
  id: number;
  group_id: number;
  name: string;
  group_name: string;
  owner_email?: string;
  group_type: "links" | "notes";
  tiles: NormalizedTile[];
  notes: Note[];
  position?: number;
}

export interface WorkspaceWithDetails extends NormalizedWorkspace {
  groups: NormalizedGroup[];
  dashboard: WorkspaceDetails;
}

/** `WorkspaceBase` (POST /workspaces), less the owner the service adds. */
export interface CreateWorkspaceData {
  workspace_name: string;
  view?: string;
  description?: string | null;
}

/** `WorkspacePatch` (PATCH /workspaces/{id}): only the fields to change. */
export interface UpdateWorkspaceData {
  workspace_name?: string;
  view?: string;
  description?: string | null;
}

/**
 * A dashboard group (`LinkGroupRead` / `NoteGroupRead`) as a WorkspaceGroup.
 * The wire names it id/name; group_id/group_name are accepted too.
 */
function toWorkspaceGroup(
  g: DashboardGroupRead,
  group_type: "links" | "notes",
): WorkspaceGroup {
  const id = g.group_id ?? g.id;
  const name = g.group_name ?? g.name;
  return {
    id,
    group_id: id,
    name,
    group_name: name,
    owner_email: g.owner_email,
    group_type,
    tiles: group_type === "links" ? (g.tiles || []).map(normalizeTile) : [],
    notes: group_type === "notes" ? g.notes || [] : [],
    position: g.position,
  };
}

/**
 * Service for workspace operations
 */
export class WorkspaceService {
  /**
   * Get all workspaces (simplified list)
   */
  async getWorkspaces(
    onFreshData?: (data: NormalizedWorkspace[]) => void,
  ): Promise<NormalizedWorkspace[]> {
    return withCache(
      "workspaces",
      async () => {
        const workspaces = await apiClient.get<RawWorkspace[]>(
          API_ENDPOINTS.WORKSPACES,
        );
        // Ensure we always have an array
        const workspaceArray = Array.isArray(workspaces) ? workspaces : [];
        return workspaceArray.map((ws, index) => normalizeWorkspace(ws, index));
      },
      { ttl: 60000, onFreshData }, // 1 minute cache
    );
  }

  /**
   * Get single workspace data (alias for getWorkspaceDetails)
   */
  async getWorkspace(workspaceId: number): Promise<WorkspaceDetails> {
    return await this.getWorkspaceDetails(workspaceId);
  }

  /**
   * Get detailed workspace data with groups and content
   */
  async getWorkspaceDetails(workspaceId: number): Promise<WorkspaceDetails> {
    return withCache(
      `workspace-details-${workspaceId}`,
      async () => {
        const workspace = await apiClient.get<WorkspaceWithDashboardRead>(
          `${API_ENDPOINTS.WORKSPACES}${workspaceId}`,
        );

        // Normalize and sort the dashboard structure
        const normalizedTileGroups =
          workspace.tile_groups_v1?.map(normalizeGroup) || [];
        const normalizedNoteGroups =
          workspace.note_groups_v1?.map(normalizeGroup) || [];

        return {
          ...workspace,
          tile_groups_v1: sortGroupsAndTiles(normalizedTileGroups),
          note_groups_v1: sortGroupsAndTiles(normalizedNoteGroups),
          note_reminders: workspace.note_reminders || [],
        };
      },
      { ttl: 30000 }, // 30 seconds cache
    );
  }

  /**
   * Get all workspaces with their groups (tiles + notes) embedded.
   * Single API call — the backend returns each workspace with its
   * `tile_groups_v1` / `note_groups_v1` / `note_reminders` inline.
   */
  async getAllWorkspacesWithGroups(options?: {
    forceRefresh?: boolean;
    onFreshData?: (data: WorkspaceWithGroups[]) => void;
  }): Promise<WorkspaceWithGroups[]> {
    const { forceRefresh, onFreshData } = options || {};
    return withCache(
      "workspaces-with-groups",
      async () => {
        const response = await apiClient.get<RawWorkspace[]>(
          API_ENDPOINTS.WORKSPACES,
        );
        const workspaces = Array.isArray(response) ? response : [];

        return workspaces.map((ws, index): WorkspaceWithGroups => {
          const normalized = normalizeWorkspace(ws, index);
          const tileGroups = (ws.tile_groups_v1 || []).map((g) =>
            toWorkspaceGroup(g, "links"),
          );
          const noteGroups = (ws.note_groups_v1 || []).map((g) =>
            toWorkspaceGroup(g, "notes"),
          );

          return {
            ...normalized,
            groups: sortGroupsAndTiles([...tileGroups, ...noteGroups]),
          };
        });
      },
      { ttl: 30000, forceRefresh, onFreshData }, // 30 seconds cache
    );
  }

  /**
   * Get all workspaces with their complete dashboard data
   */
  async getAllWorkspacesWithDetails(): Promise<WorkspaceWithDetails[]> {
    return withCache(
      "workspaces-with-details",
      async () => {
        const workspaces = await this.getWorkspaces();

        const workspacesWithDetails = await Promise.all(
          workspaces.map(async (workspace): Promise<WorkspaceWithDetails> => {
            try {
              const details = await this.getWorkspaceDetails(
                workspace.workspace_id,
              );

              // Combine and sort all groups (tile_groups_v1 and note_groups_v1 are already sorted)
              const allGroups = [
                ...(details.tile_groups_v1 || []),
                ...(details.note_groups_v1 || []),
              ];

              return {
                ...workspace,
                groups: sortGroupsAndTiles(allGroups),
                dashboard: details,
              };
            } catch (error) {
              logger.warn(
                `Failed to load details for workspace ${workspace.workspace_id}:`,
                error,
              );
              return {
                ...workspace,
                groups: [],
                dashboard: {
                  workspace_id: workspace.workspace_id,
                  workspace_name: workspace.workspace_name || "",
                  view: workspace.view || "personal",
                  owner_email: workspace.owner_email || "",
                  tile_groups_v1: [],
                  note_groups_v1: [],
                  note_reminders: [],
                },
              };
            }
          }),
        );

        return workspacesWithDetails;
      },
      { ttl: 30000 }, // 30 seconds cache
    );
  }

  /**
   * Create a new workspace
   */
  async createWorkspace(
    workspaceData: CreateWorkspaceData,
    userEmail: string,
  ): Promise<NormalizedWorkspace> {
    const processedData = {
      ...workspaceData,
      owner_email: userEmail,
      view: workspaceData.view || "personal",
    };

    const newWorkspace = await apiClient.post<RawWorkspace>(
      API_ENDPOINTS.WORKSPACES,
      processedData,
    );

    // Invalidate all workspace-related caches after creating
    cache.remove(CACHE_KEYS.WORKSPACES);
    cache.remove("workspaces-with-groups");
    cache.remove("workspaces-with-details");

    return normalizeWorkspace(newWorkspace);
  }

  /**
   * Update an existing workspace
   */
  async updateWorkspace(
    workspaceId: number,
    workspaceData: UpdateWorkspaceData,
  ): Promise<NormalizedWorkspace> {
    const updatedWorkspace = await apiClient.patch<RawWorkspace>(
      `${API_ENDPOINTS.WORKSPACES}${workspaceId}`,
      workspaceData,
    );

    // Invalidate all workspace-related caches after updating
    cache.remove(CACHE_KEYS.WORKSPACES);
    cache.remove("workspaces-with-groups");
    cache.remove("workspaces-with-details");

    return normalizeWorkspace(updatedWorkspace);
  }

  /**
   * Delete a workspace
   */
  async deleteWorkspace(workspaceId: number): Promise<number> {
    await apiClient.delete(`${API_ENDPOINTS.WORKSPACES}${workspaceId}`);

    // Invalidate all workspace-related caches after deleting
    cache.remove(CACHE_KEYS.WORKSPACES);
    cache.remove("workspaces-with-groups");
    cache.remove("workspaces-with-details");

    return workspaceId;
  }
}

// Create singleton instance
export const workspaceService = new WorkspaceService();
